import React, { useEffect, useRef, useState } from 'react';
import { Card, Row, Col, Statistic, Table, Typography, Progress, Select, Button } from 'antd';
import { UserOutlined, TeamOutlined, PercentageOutlined } from '@ant-design/icons';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';

// Register the datalabels plugin
Chart.register(ChartDataLabels);

const { Title, Text } = Typography;

/**
 * Component to display customer buying behavior statistics
 * @param {Object} props - Component props
 * @param {Object} props.buyingBehaviorData - Customer buying behavior data
 * @returns {JSX.Element} Customer buying behavior statistics
 */
const CustomerBuyingBehavior = ({ buyingBehaviorData }) => {
  const monthlyBreakdownChartRef = useRef(null);
  const monthlyRatioChartRef = useRef(null);
  const [showFullTable, setShowFullTable] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState("last_30_days");
  
  // Extract data safely with defaults
  const {
    mono_buyers = 0,
    multi_buyers = 0,
    total_customers = 0,
    mono_buyer_ratio = 0,
    multi_buyer_ratio = 0,
    monthly_breakdown = [],
    category_breakdown = [],
    purchase_distribution = [],
    recency_distribution = {}
  } = buyingBehaviorData || {};
console.log(buyingBehaviorData, "buyingBehaviorData")

  // Prepare data for monthly breakdown chart
  const monthlyLabels = monthly_breakdown.map(month => month.month);
  const monoBuyersData = monthly_breakdown.map(month => month.mono_buyers);
  const multiBuyersData = monthly_breakdown.map(month => month.multi_buyers);
  const monoRatioData = monthly_breakdown.map(month => month.mono_buyer_ratio);
  const multiRatioData = monthly_breakdown.map(month => month.multi_buyer_ratio);

  useEffect(() => {
    console.log(buyingBehaviorData, "buyingBehaviorData")
    if (!buyingBehaviorData?.monthly_breakdown) return;
    
    // Create monthly breakdown chart
    const createMonthlyBreakdownChart = () => {
      const ctx = document.getElementById('monthlyBreakdownChart');
      if (!ctx) return;
      
      // Destroy existing chart if it exists
      if (monthlyBreakdownChartRef.current) {
        monthlyBreakdownChartRef.current.destroy();
      }
      
      monthlyBreakdownChartRef.current = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: monthlyLabels,
          datasets: [
            {
              label: 'Mono-buyers',
              data: monoBuyersData,
              backgroundColor: '#8884d8',
              borderColor: '#7771d3',
              borderWidth: 1
            },
            {
              label: 'Multi-buyers',
              data: multiBuyersData,
              backgroundColor: '#82ca9d',
              borderColor: '#71c08e',
              borderWidth: 1
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: 'Number of Customers'
              }
            },
            x: {
              title: {
                display: true,
                text: 'Month'
              }
            }
          },
          plugins: {
            title: {
              display: true,
              text: 'Monthly Distribution of Mono vs Multi Buyers'
            },
            tooltip: {
              mode: 'index',
              intersect: false
            },
            legend: {
              position: 'top'
            }
          }
        }
      });
    };
    
    // Create monthly ratio chart
    const createMonthlyRatioChart = () => {
      const ctx = document.getElementById('monthlyRatioChart');
      if (!ctx) return;
      
      // Destroy existing chart if it exists
      if (monthlyRatioChartRef.current) {
        monthlyRatioChartRef.current.destroy();
      }
      
      monthlyRatioChartRef.current = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: monthlyLabels,
          datasets: [
            {
              label: 'Mono-buyer Ratio (%)',
              data: monoRatioData,
              backgroundColor: '#ff7300',
              borderColor: '#e66900',
              borderWidth: 1
            },
            {
              label: 'Multi-buyer Ratio (%)',
              data: multiRatioData,
              backgroundColor: '#0088fe',
              borderColor: '#0077e6',
              borderWidth: 1
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              beginAtZero: true,
              max: 100,
              title: {
                display: true,
                text: 'Percentage (%)'
              }
            },
            x: {
              title: {
                display: true,
                text: 'Month'
              }
            }
          },
          plugins: {
            title: {
              display: true,
              text: 'Monthly Ratio Trends'
            },
            tooltip: {
              mode: 'index',
              intersect: false,
              callbacks: {
                label: function(context) {
                  return context.dataset.label + ': ' + context.raw.toFixed(2) + '%';
                }
              }
            },
            legend: {
              position: 'top'
            }
          }
        }
      });
    };
    
    // Create charts after a short delay to ensure the DOM is ready
    setTimeout(() => {
      createMonthlyBreakdownChart();
      createMonthlyRatioChart();
    }, 100);
    
    // Cleanup function
    return () => {
      if (monthlyBreakdownChartRef.current) {
        monthlyBreakdownChartRef.current.destroy();
      }
      if (monthlyRatioChartRef.current) {
        monthlyRatioChartRef.current.destroy();
      }
    };
  }, [buyingBehaviorData, monthlyLabels, monoBuyersData, multiBuyersData, monoRatioData, multiRatioData]);
  
  // Return early if no data
  if (!buyingBehaviorData) {
    return <div>No customer buying behavior data available</div>;
  }

  // Function to prepare purchase distribution data with aggregation for the 10th row
  const preparePurchaseDistributionData = (data) => {
    if (!data || data.length === 0) return [];
    if (showFullTable) return data.map((item, index) => ({ ...item, key: index }));
    
    if (data.length <= 10) return data.map((item, index) => ({ ...item, key: index }));
    
    // Take first 9 items
    const first9Items = data.slice(0, 9).map((item, index) => ({ ...item, key: index }));
    
    // Aggregate remaining items (10th to last)
    const remainingItems = data.slice(9);
    const aggregatedItem = remainingItems.reduce((acc, item) => {
      return {
        purchase_count: '10+',
        customer_count: (acc.customer_count || 0) + item.customer_count,
        percentage: (acc.percentage || 0) + item.percentage,
        key: 9
      };
    }, {});
    
    return [...first9Items, aggregatedItem];
  };

  // Prepare data for category breakdown
  const categoryColumns = [
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
    },
    {
      title: 'Customer Count',
      dataIndex: 'customer_count',
      key: 'customer_count',
      sorter: (a, b) => a.customer_count - b.customer_count,
    },
    {
      title: 'Percentage',
      key: 'percentage',
      render: (_, record) => {
        const percentage = Math.round((record.customer_count / total_customers) * 100);
        return (
          <div style={{ width: '100%' }}>
            <Progress 
              percent={percentage} 
              size="small" 
              status="active" 
              format={() => `${percentage}%`}
            />
          </div>
        );
      },
    },
  ];

  return (
    <div className="customer-buying-behavior">
 

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} md={8}>
          <Card>
            <Statistic
              title="Mono-buyers"
              value={mono_buyers}
              prefix={<UserOutlined />}
              suffix={<small>{` (${mono_buyer_ratio}%)`}</small>}
            />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic
              title="Multi-buyers"
              value={multi_buyers}
              prefix={<TeamOutlined />}
              suffix={<small>{` (${multi_buyer_ratio}%)`}</small>}
            />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24}>
          <Card title="Monthly Breakdown of Mono vs Multi Buyers">
            <div style={{ height: '400px', position: 'relative' }}>
              <canvas id="monthlyBreakdownChart" height="400"></canvas>
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24}>
          <Card title="Monthly Ratio Trends">
            <div style={{ height: '400px', position: 'relative' }}>
              <canvas id="monthlyRatioChart" height="400"></canvas>
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24}>
          <Card 
            title="Customer Distribution by Purchase Count"
            actions={[
              <div key="showMore" style={{ textAlign: 'center', padding: '10px 0' }}>
                <Button 
                  type="primary" 
                  onClick={() => setShowFullTable(!showFullTable)}
                >
                  {showFullTable ? 'Show Less' : 'Show More'}
                </Button>
              </div>
            ]}
          >
            <Table
              columns={[
                {
                  title: 'Purchase Count',
                  dataIndex: 'purchase_count',
                  key: 'purchase_count',
                  sorter: (a, b) => a.purchase_count - b.purchase_count,
                },
                {
                  title: 'Number of Customers',
                  dataIndex: 'customer_count',
                  key: 'customer_count',
                  sorter: (a, b) => a.customer_count - b.customer_count,
                },
                {
                  title: 'Percentage',
                  dataIndex: 'percentage',
                  key: 'percentage',
                  render: (text) => `${Number(text).toFixed(2)}%`,
                  sorter: (a, b) => a.percentage - b.percentage,
                },
                {
                  title: 'Distribution',
                  key: 'distribution',
                  render: (_, record) => (
                    <Progress 
                      percent={record.percentage} 
                      size="small" 
                      status="active" 
                      format={() => `${Number(record.percentage).toFixed(2)}%`}
                    />
                  ),
                },
              ]}
              dataSource={preparePurchaseDistributionData(purchase_distribution)}
              pagination={showFullTable ? { pageSize: 10 } : false}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24}>
          <Card title="Customer Purchase Recency Distribution">
            {recency_distribution && (
              <>
                <div style={{ marginBottom: 16 }}>
                  <Select
                    defaultValue="last_30_days"
                    style={{ width: 200 }}
                    onChange={(value) => setSelectedPeriod(value)}
                  >
                    {Object.keys(recency_distribution).sort((a, b) => {
                      // Custom sorting function to order periods logically
                      const orderMap = {
                        'last_30_days': 1,
                        'last_60_days': 2,
                        'last_90_days': 3,
                        'last_180_days': 4,
                        'last_270_days': 5,
                        'last_1_year': 6,
                        'last_1_5_years': 7,
                        'last_2_years': 8,
                        'last_3_plus_years': 9
                      };
                      return (orderMap[a] || 100) - (orderMap[b] || 100);
                    }).map((periodKey) => {
                      let label;
                      // Day-based periods
                      if (periodKey === 'last_30_days') {
                        label = 'Last 30 Days';
                      } else if (periodKey === 'last_60_days') {
                        label = 'Last 60 Days';
                      } else if (periodKey === 'last_90_days') {
                        label = 'Last 90 Days';
                      } else if (periodKey === 'last_180_days') {
                        label = 'Last 180 Days';
                      } else if (periodKey === 'last_270_days') {
                        label = 'Last 270 Days';
                      }
                      // Year-based periods
                      else if (periodKey === 'last_1_year') {
                        label = 'Last 1 Year';
                      } else if (periodKey === 'last_1_5_years') {
                        label = 'Last 1.5 Years';
                      } else if (periodKey === 'last_2_years') {
                        label = 'Last 2 Years';
                      } else if (periodKey === 'last_3_plus_years') {
                        label = 'Last 3+ Years';
                      } else {
                        label = periodKey;
                      }
                      return (
                        <Select.Option key={periodKey} value={periodKey}>
                          {label}
                        </Select.Option>
                      );
                    })}
                  </Select>
                </div>
                {selectedPeriod && recency_distribution[selectedPeriod] && (
                  <Table
                      columns={[
                        {
                          title: 'Purchase Count',
                          dataIndex: 'purchase_count',
                          key: 'purchase_count',
                          sorter: (a, b) => a.purchase_count - b.purchase_count,
                        },
                        {
                          title: 'Number of Customers',
                          dataIndex: 'customer_count',
                          key: 'customer_count',
                          sorter: (a, b) => a.customer_count - b.customer_count,
                        },
                        {
                          title: 'Percentage',
                          dataIndex: 'percentage',
                          key: 'percentage',
                          render: (text) => `${Number(text).toFixed(2)}%`,
                          sorter: (a, b) => a.percentage - b.percentage,
                        },
                        {
                          title: 'Distribution',
                          key: 'distribution',
                          render: (_, record) => (
                            <Progress 
                              percent={record.percentage} 
                              size="small" 
                              status="active" 
                              format={() => `${Number(record.percentage).toFixed(2)}%`}
                            />
                          ),
                        },
                      ]}
                      dataSource={recency_distribution[selectedPeriod].distribution.map((item, index) => ({ ...item, key: index }))}
                      pagination={{ pageSize: 10 }}
                      summary={() => (
                        <Table.Summary fixed>
                          <Table.Summary.Row>
                            <Table.Summary.Cell index={0}>
                              <Text strong>Total Customers in Period</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={1}>
                              <Text strong>{recency_distribution[selectedPeriod].total_customers}</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={2}></Table.Summary.Cell>
                            <Table.Summary.Cell index={3}></Table.Summary.Cell>
                          </Table.Summary.Row>
                        </Table.Summary>
                      )}
                    />
                )}
              </>
            )}
          </Card>
        </Col>
      </Row>
      
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24}>
          <Card title="Customer Distribution by Category">
            <Table
              columns={categoryColumns}
              dataSource={category_breakdown.map((item, index) => ({ ...item, key: index }))}
              pagination={{ pageSize: 10 }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default CustomerBuyingBehavior;
